<template>
  <div class="ui-input-text component">
    <div v-if="$slots.before" class="slot-before">
      <slot name="before"></slot>
    </div>

    <input
      ref="input"
      v-maska="$attrs.maska"
      :class="inputClasses"
      v-bind="attrs"
      :type="type"
      v-on="listeners"
    />
    <label v-if="!staticPlaceholder">{{ placeholder }} </label>

    <div v-if="$slots.bottom" class="slot-bottom">
      <slot name="bottom" />
    </div>

    <div v-if="isPasswordReveal" class="slot-after">
      <div> <UiInputPasswordTypeSwitcher /> </div>
    </div>

    <div v-if="$slots.after" class="slot-after">
      <slot name="after" />
    </div>
  </div>
</template>

<script>
import { maska } from 'maska'

export default {
  name: 'UiInputText',
  directives: { maska },
  components: {
    UiInputPasswordTypeSwitcher: () => import('@/components/UiInputPasswordTypeSwitcher.vue'),
  },
  inheritAttrs: false,
  props: {
    placeholder: { type: String, default: 'Ваш текст' },
    staticPlaceholder: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
      validator(value) {
        return ['email', 'number', 'password', 'search', 'tel', 'text', 'url'].includes(value)
      },
    },
  },
  computed: {
    inputClasses() {
      return {
        'border-radius-start': !this.$slots.before,
        'border-radius-end': !this.$slots.after && !this.isPasswordReveal,
        'is-focused': this.hasValue,
        'static-placeholder': this.staticPlaceholder,
      }
    },
    isPasswordReveal() {
      return 'password-reveal' in this.$attrs
    },
    hasValue() {
      return this.$attrs && this.$attrs.value && this.$attrs.value.toString().length
    },
    attrs() {
      return {
        ...this.$attrs,
        placeholder: this.staticPlaceholder ? this.placeholder : null,
      }
    },
    listeners() {
      return {
        ...this.$listeners,
        input: (event) => this.$emit('input', event.target.value),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$color-gray: #b3b3b3;
$radius: 5px;
$padding: 5px;

// Selectors for manipulate input borders
$borders: '
  &.border-radius-start,
  &.border-radius-end,
  ~ .slot-before,
  ~ .slot-after
';

.ui-input-text.component {
  position: relative;
  box-shadow: none;

  @include createFlex(flexbox, nowrap, row, flex-start, stretch, stretch);

  .slot-bottom {
    padding-top: $padding;
    font-size: rem(13px);
    color: $color-gray;
  }
  .slot-after,
  .slot-before {
    cursor: pointer;
    border: 1px solid transparent;

    @include unselectable();

    > div {
      @include square(44px);
      @include createFlex(flexbox, nowrap, row, center, center, center);
    }
  }

  .slot-before {
    border: 1px solid transparent;
    border-right: 0;
    border-top-left-radius: $radius;
    border-bottom-left-radius: $radius;
  }
  .slot-after {
    background-color: $input-background;
    border: 1px solid transparent;
    border-left: 0;
    border-top-right-radius: $radius;
    border-bottom-right-radius: $radius;
  }

  label {
    position: absolute;
    top: rem(18px);
    left: rem(16px);
    font-size: rem(11px);
    color: $color-gray;
    pointer-events: none;
    cursor: text;

    @include unselectable();
  }

  .static-placeholder {
    height: 32px;
    padding: rem(8px) rem(12px);
  }

  input {
    width: 100%;
    height: 46px;
    padding: rem(22px) rem(16px) rem(6px);
    font-family: 'SF Pro Text', Lato, Helvetica, Arial, sans-serif;
    font-size: rem(12px);
    color: $color-gray-600;
    background-color: $input-background;
    border: 1px solid transparent;
    border-right: 0;
    border-left: 0;
    outline: none;
    box-shadow: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    &.border-radius-start {
      border-left: 1px solid transparent;
      -webkit-border-radius: $radius;
      -moz-border-radius: $radius;
      border-radius: $radius;
      -webkit-border-top-right-radius: 0;
      border-top-right-radius: 0;
      -webkit-border-bottom-right-radius: 0;
      border-bottom-right-radius: 0;
      -moz-border-radius-topright: 0;
      -moz-border-radius-bottomright: 0;
    }
    &.border-radius-end {
      border-right: 1px solid transparent;
      -webkit-border-radius: 0;
      -moz-border-radius: 0;
      border-radius: $radius;
      -webkit-border-top-right-radius: $radius;
      border-top-right-radius: $radius;
      -webkit-border-bottom-right-radius: $radius;
      border-bottom-right-radius: $radius;
      -moz-border-radius-topright: $radius;
      -moz-border-radius-bottomright: $radius;
    }

    &:hover {
      border-color: darken($input-background, 2%);

      #{$borders} {
        border-color: darken($input-background, 2%);
      }
    }
    &:focus {
      border-color: darken($input-background, 4%);

      #{$borders} {
        border-color: darken($input-background, 4%);
      }

      ~ label {
        @extend .focused-input;
      }
    }

    &.is-focused {
      ~ label {
        @extend .focused-input;
      }
    }
  }

  // stylelint-disable no-descending-specificity
  &.is-invalid-input {
    label {
      color: $color-error;
    }

    input {
      background-color: lighten($color-error, 29%);
      border-color: $color-error;

      #{$borders} {
        background-color: lighten($color-error, 29%);
        border-color: $color-error;
      }

      &:hover {
        border-color: darken($color-error, 1%);

        #{$borders} {
          border-color: darken($color-error, 1%);
        }
      }
      &:focus {
        border-color: darken($color-error, 3%);

        #{$borders} {
          border-color: darken($color-error, 3%);
        }
      }
    }
  }

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    margin: 0;
    -webkit-appearance: none;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }

  .focused-input {
    top: rem(6px);
    left: rem(16px);

    @include transition(all 0.225s ease);
  }
}
</style>
